import { Children, ReactNode } from 'react'
import { Casing, formatCase } from '@common/helpers/casing'

const isRendered = (child: ReactNode) =>
  child !== null && child !== undefined && child !== false && child !== true

/**
 * Cases the plain-text children of a label and leaves everything else (chips, links,
 * variables rendered as elements) as given. Text after the first child continues the label,
 * so a run after an inline <strong> is not treated as its first word, and a run with more after it
 * does not treat its last word as the label's last.
 */
export const formatChildren = (casing: Casing, children: ReactNode): ReactNode => {
  if (casing === 'none') return children
  if (typeof children === 'string') return formatCase(casing, children)
  if (!Array.isArray(children)) return children
  // Flattened, so an index lines up with what follows even when a child is a .map() result.
  const items = Children.toArray(children)
  const isContent = (child: ReactNode) =>
    typeof child === 'string' ? child.trim() !== '' : isRendered(child)
  let hasStarted = false
  return items.map((child: ReactNode, index): ReactNode => {
    if (typeof child === 'string') {
      const isFollowed = items.slice(index + 1).some(isContent)
      const cased = formatCase(casing, child, { isContinuation: hasStarted, isFollowed })
      hasStarted = hasStarted || child.trim() !== ''
      return cased
    }
    if (isRendered(child)) {
      hasStarted = true
    }
    return child
  })
}
